/**
 * World Data Configuration for Midgard's Nine Realms
 * 
 * This file defines the hierarchical world structure:
 * WORLD → REALM/REGION → SETTLEMENT/AREA → LOCAL MAP → LOCATION → SOCIAL/GAME SPACE
 * 
 * The artwork/map image can be replaced without rebuilding the navigation system.
 * Region positions are defined as percentages to work with any map image.
 */

// Approved cinematic artwork — Nine Realms Gateway v2 (1672×941, not native 4K)
// Master PNG preserved at /api/files/midgard/static/nine-realms-gateway-v2.png
// WebP delivery variants optimised for network performance
export const WORLD_MAP_IMAGE = "/api/files/midgard/static/nine-realms-gateway-v2.webp";
export const WORLD_MAP_IMAGE_MOBILE = "/api/files/midgard/static/nine-realms-gateway-v2-mobile.webp";
export const WORLD_MAP_IMAGE_PNG = "/api/files/midgard/static/nine-realms-gateway-v2.png";
export const WORLD_MAP_DIMENSIONS = { width: 1672, height: 941 };

// The Nine Realms of Yggdrasil
// position = marker centre, size = elliptical interaction zone; both are percentages
// of the gateway artwork (nine-realms-gateway-v2), measured against a 5% grid.
export const REALMS = [
  {
    id: 'asgard',
    name: 'Asgard',
    subtitle: 'Home of the Æsir',
    description: 'The realm of the Norse gods, connected to Midgard by the rainbow bridge Bifröst.',
    // Golden citadel above the clouds, upper left
    position: { x: 24, y: 12 },
    size: { width: 28, height: 16 },
    theme: {
      primary: '#FFD700',
      secondary: '#87CEEB',
      accent: '#FFFFFF',
      bgGradient: 'from-blue-900/80 via-indigo-900/60 to-slate-900/80',
    },
    settlements: [],
    unlocked: true,
  },
  {
    id: 'vanaheim',
    name: 'Vanaheim',
    subtitle: 'Home of the Vanir',
    description: 'The realm of the Vanir gods, masters of sorcery, fertility and wisdom.',
    // Green river valley and giant trees, left
    position: { x: 18, y: 31 },
    size: { width: 32, height: 20 },
    theme: {
      primary: '#228B22',
      secondary: '#90EE90',
      accent: '#FAFAD2',
      bgGradient: 'from-green-900/80 via-emerald-900/60 to-teal-900/80',
    },
    settlements: [],
    unlocked: true,
  },
  {
    id: 'alfheim',
    name: 'Alfheim',
    subtitle: 'Home of the Elves',
    description: 'The realm of the Light Elves, a place of ethereal beauty and magic.',
    // White-blossom groves under the pale moon, upper right
    position: { x: 73, y: 12 },
    size: { width: 28, height: 16 },
    theme: {
      primary: '#E6E6FA',
      secondary: '#DDA0DD',
      accent: '#FFB6C1',
      bgGradient: 'from-purple-900/80 via-pink-900/60 to-indigo-900/80',
    },
    settlements: [],
    unlocked: true,
  },
  {
    id: 'jotunheim-north',
    name: 'Jötunheim',
    subtitle: 'Land of the Sun',
    description: 'The northern reaches of Jötunheim, land of the sun giants.',
    // Provisional: the art shows nine realms and none for this entry; it sits on Jötunheim's highest peak
    position: { x: 57, y: 29 },
    size: { width: 9, height: 9 },
    theme: {
      primary: '#4682B4',
      secondary: '#B0C4DE',
      accent: '#F0F8FF',
      bgGradient: 'from-slate-800/80 via-blue-900/60 to-gray-900/80',
    },
    settlements: [],
    unlocked: true,
  },
  {
    id: 'jotunheim',
    name: 'Jötunheim',
    subtitle: 'Realm of the Giants',
    description: 'The harsh, mountainous realm of the Frost Giants and other Jötnar.',
    // Snow-capped mountain massif, right of the trunk
    position: { x: 62, y: 42 },
    size: { width: 17, height: 18 },
    theme: {
      primary: '#4682B4',
      secondary: '#B0C4DE',
      accent: '#F0F8FF',
      bgGradient: 'from-slate-800/80 via-blue-900/60 to-gray-900/80',
    },
    settlements: [],
    unlocked: true,
  },
  {
    id: 'midgard',
    name: 'Midgard',
    subtitle: 'Realm of Humans',
    description: 'The world of humanity, connected to all realms through Yggdrasil.',
    // Viking village, fjord and longship, lower centre
    position: { x: 47, y: 70 },
    size: { width: 30, height: 22 },
    theme: {
      primary: '#8B4513',
      secondary: '#D2691E',
      accent: '#F5DEB3',
      bgGradient: 'from-amber-900/80 via-orange-900/60 to-yellow-900/80',
    },
    settlements: [],
    unlocked: true,
  },
  {
    id: 'svartalfheim',
    name: 'Svartálfheim',
    subtitle: 'Home of the Dwarves',
    description: 'The underground realm of the Dwarves, master smiths and craftsmen.',
    // Forge city inside the great cave, lower left
    position: { x: 14, y: 62 },
    size: { width: 28, height: 28 },
    theme: {
      primary: '#8B0000',
      secondary: '#FF4500',
      accent: '#FFA500',
      bgGradient: 'from-red-950/80 via-orange-950/60 to-amber-950/80',
    },
    settlements: [],
    unlocked: true,
  },
  {
    id: 'niflheim',
    name: 'Niflheim',
    subtitle: 'Realm of Ice and Mist',
    description: 'The primordial realm of ice, cold and darkness. Origin of all rivers.',
    // Cracked ice plain and frozen falls, right
    position: { x: 86, y: 47 },
    size: { width: 27, height: 17 },
    theme: {
      primary: '#ADD8E6',
      secondary: '#E0FFFF',
      accent: '#FFFFFF',
      bgGradient: 'from-cyan-900/80 via-blue-950/60 to-slate-950/80',
    },
    settlements: [],
    unlocked: true,
  },
  {
    id: 'muspelheim',
    name: 'Muspelheim',
    subtitle: 'Realm of Fire',
    description: 'The primordial realm of fire and heat, ruled by the fire giant Surtr.',
    // Lava fields and volcanoes under the smoke, upper right
    position: { x: 85, y: 27 },
    size: { width: 28, height: 13 },
    theme: {
      primary: '#FF4500',
      secondary: '#FF6347',
      accent: '#FFD700',
      bgGradient: 'from-red-900/80 via-orange-800/60 to-yellow-900/80',
    },
    settlements: [],
    unlocked: true,
  },
  {
    id: 'helheim',
    name: 'Helheim',
    subtitle: 'Realm of the Dead',
    description: 'The realm of those who died of illness or old age, ruled by the goddess Hel.',
    // Green-misted dead forest, lower right
    position: { x: 84, y: 73 },
    size: { width: 30, height: 24 },
    theme: {
      primary: '#2F4F4F',
      secondary: '#696969',
      accent: '#9370DB',
      bgGradient: 'from-gray-950/80 via-slate-900/60 to-zinc-950/80',
    },
    settlements: [],
    unlocked: true,
  },
];

// Get a realm by its ID
export const getRealmById = (id) => REALMS.find(r => r.id === id);

// World hierarchy levels (for future expansion)
export const HIERARCHY_LEVELS = {
  WORLD: 'world',
  REALM: 'realm',
  SETTLEMENT: 'settlement',
  LOCAL_MAP: 'local_map',
  LOCATION: 'location',
  SOCIAL_SPACE: 'social_space',
};

// Placeholder for future location types within realms
export const LOCATION_TYPES = {
  TOWN: 'town',
  VILLAGE: 'village',
  CITY: 'city',
  WILDERNESS: 'wilderness',
  ROAD: 'road',
  RIVER: 'river',
  MOUNTAIN: 'mountain',
  BUILDING: 'building',
  PLAYER_HOUSE: 'player_house',
  SHOP: 'shop',
  TAVERN: 'tavern',
  DUNGEON: 'dungeon',
};

// Placeholder location data for each realm (can be expanded later)
// NOTE: Midgard uses midgardData.js for its full hierarchy
export const REALM_LOCATIONS = {
  asgard: [
    { id: 'bifrost', name: 'Bifröst Bridge', type: LOCATION_TYPES.ROAD, locked: false },
    { id: 'valhalla', name: 'Valhalla', type: LOCATION_TYPES.BUILDING, locked: true },
    { id: 'gladsheim', name: 'Gladsheim', type: LOCATION_TYPES.CITY, locked: true },
  ],
  // Midgard is now fully implemented in midgardData.js with regions, settlements, and locations
  midgard: [],
  vanaheim: [
    { id: 'grove', name: 'Sacred Grove', type: LOCATION_TYPES.WILDERNESS, locked: false },
    { id: 'harbor', name: 'Vanaheim Harbor', type: LOCATION_TYPES.TOWN, locked: true },
  ],
  alfheim: [
    { id: 'palace', name: 'Crystal Palace', type: LOCATION_TYPES.BUILDING, locked: true },
    { id: 'gardens', name: 'Eternal Gardens', type: LOCATION_TYPES.WILDERNESS, locked: false },
  ],
  jotunheim: [
    { id: 'utgard', name: 'Útgarðr Fortress', type: LOCATION_TYPES.CITY, locked: true },
    { id: 'mountains', name: 'Frost Mountains', type: LOCATION_TYPES.MOUNTAIN, locked: false },
  ],
  svartalfheim: [
    { id: 'forges', name: 'The Great Forges', type: LOCATION_TYPES.BUILDING, locked: false },
    { id: 'mines', name: 'Deep Mines', type: LOCATION_TYPES.DUNGEON, locked: true },
  ],
  niflheim: [
    { id: 'river', name: 'River Élivágar', type: LOCATION_TYPES.RIVER, locked: false },
    { id: 'well', name: 'Hvergelmir Well', type: LOCATION_TYPES.BUILDING, locked: true },
  ],
  muspelheim: [
    { id: 'gates', name: 'Gates of Fire', type: LOCATION_TYPES.BUILDING, locked: false },
    { id: 'throne', name: 'Surtr\'s Throne', type: LOCATION_TYPES.BUILDING, locked: true },
  ],
  helheim: [
    { id: 'gates', name: 'Hel\'s Gate', type: LOCATION_TYPES.BUILDING, locked: false },
    { id: 'hall', name: 'Éljúðnir Hall', type: LOCATION_TYPES.BUILDING, locked: true },
  ],
};

// Get locations for a realm
export const getRealmLocations = (realmId) => REALM_LOCATIONS[realmId] || [];
